import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown, Zap, Layers, Gauge } from "lucide-react";

const lines = ["Instant Bulk DA & PA", "Intelligence Powered", "by the MOZ Index"];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.15 + i * 0.14, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden bg-grid-pattern pt-16"
    >
      <div className="noise-overlay" />
      <div className="absolute -top-40 right-[-10%] w-[34rem] h-[34rem] rounded-full bg-[#D4FF00]/[0.07] blur-[120px]" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 w-full relative">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#D4FF00] mb-8"
        >
          // Bulk Authority Checker for SEO Specialists
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.08] text-[#F3F4F6] max-w-5xl"
        >
          {lines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariant}
                initial="hidden"
                animate="visible"
              >
                {i === 2 ? (
                  <>
                    by the <span className="text-[#D4FF00]">MOZ</span> Index
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="mt-8 text-lg text-[#9CA3AF] leading-relaxed max-w-xl"
        >
          Audit domain authority, page authority, and spam scores for up to 50 URLs
          in one click. No signup, no bloat — paste, check, export.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-check-button"
            onClick={() => document.getElementById("checker")?.scrollIntoView({ behavior: "smooth" })}
            className="group flex items-center gap-2 px-7 py-3.5 rounded-md bg-[#D4FF00] text-[#0B0F17] font-bold text-base hover:bg-[#E5FF66] active:bg-[#BCE600] transition-colors shadow-[0_0_40px_rgba(212,255,0,0.18)]"
          >
            Check URLs Now
            <ArrowDown size={18} className="group-hover:translate-y-0.5 transition-transform" />
          </button>
          <span className="font-mono2 text-xs text-[#6B7280]">Free &middot; No account needed</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.05, duration: 0.7 }}
          className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#263248] border border-[#263248] max-w-2xl"
        >
          {[
            { icon: Gauge, label: "Index Coverage", value: "MOZ Link Graph", testid: "hero-stat-accuracy" },
            { icon: Layers, label: "Batch Capacity", value: "50 URLs / run", testid: "hero-stat-batch-capacity" },
            { icon: Zap, label: "Time to Results", value: "< 1.2 seconds", testid: "hero-stat-speed" },
          ].map(({ icon: Icon, label, value, testid }) => (
            <div key={label} data-testid={testid} className="bg-[#0B0F17] px-5 py-4">
              <Icon size={16} className="text-[#D4FF00] mb-2" />
              <div className="font-mono2 text-sm font-bold text-[#F3F4F6]">{value}</div>
              <div className="text-xs text-[#6B7280] mt-0.5">{label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.div
        style={{ y: badgeY }}
        className="hidden lg:block absolute right-16 top-1/3 rotate-6"
      >
        <div className="border border-[#263248] bg-[#121824]/80 backdrop-blur px-5 py-4 rounded-lg">
          <div className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">moz.com</div>
          <div className="flex items-end gap-3 font-mono2">
            <span className="text-3xl font-bold text-[#D4FF00]">DA 91</span>
            <span className="text-sm text-[#4ADE80] mb-1">&#9650; strong</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
